import { IconX } from "@tabler/icons-react";
import { MotionConfig, motion, useDragControls, type PanInfo } from "motion/react";
import { useEffect, useRef, type PropsWithChildren, type ReactNode } from "react";
import { toast } from "react-toastify";
import { sheetSpring } from "src/core/components/frame/frameMotion";
import { Portal } from "src/core/components/portals/Portal";
import { useIsWide } from "src/core/hooks/useIsWide";

interface Props {
    open: boolean;
    onClose(): void;

    title: string;
    subtitle?: ReactNode;

    /** Actions pinned under the content, such as Cancel and Save */
    footer?: ReactNode;

    /** On a laptop: from the right to work on one thing, in the middle to confirm or choose */
    placement?: "side" | "centre";
}

/**
 * Every modal in the app. One job, for one thing, until it is dismissed: from the bottom on a
 * phone, where it can be dragged away, and on a laptop from the right, or in the middle for a
 * confirmation or a short choice. A native dialog, as the More sheet is, so focus, Escape and the
 * top layer behave as every other modal does; `motion` only moves it.
 */
export const Sheet = ({
    open,
    onClose,
    title,
    subtitle,
    footer,
    placement = "side",
    children,
}: PropsWithChildren<Props>) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const dragControls = useDragControls();
    const wide = useIsWide();
    const side = wide && placement === "side";
    const centre = wide && placement === "centre";

    useEffect(() => {
        const dialog = dialogRef.current;

        if (open && dialog && !dialog.open) {
            // a toast would otherwise sit over the sheet's own buttons
            toast.dismiss();
            dialog.showModal();

            // React's `autoFocus` runs while the dialog is still shut, where focus can't land
            dialog.querySelector<HTMLElement>("[data-autofocus]")?.focus();
        }
    }, [open]);

    /** Escape asks to close, so the sheet can slide away before the dialog shuts */
    useEffect(() => {
        const dialog = dialogRef.current;
        const onCancel = (event: Event) => {
            event.preventDefault();
            onClose();
        };

        dialog?.addEventListener("cancel", onCancel);

        return () => dialog?.removeEventListener("cancel", onCancel);
    }, [onClose]);

    const onDragEnd = (_: unknown, info: PanInfo) => {
        if (info.offset.y > 100 || info.velocity.y > 600) onClose();
    };

    const away = centre
        ? { x: 0, y: 0, opacity: 0, scale: 0.96 }
        : side
          ? { x: "105%", y: 0, opacity: 1, scale: 1 }
          : { x: 0, y: "105%", opacity: 1, scale: 1 };

    return (
        <Portal>
            <MotionConfig reducedMotion="user">
                <dialog
                    ref={dialogRef}
                    aria-label={title}
                    className="fixed inset-0 m-0 p-0 w-full h-full max-w-none max-h-none bg-transparent backdrop:bg-transparent overflow-hidden"
                >
                    <motion.div
                        className="absolute inset-0 bg-black/30"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: open ? 1 : 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onClose}
                    />

                    <motion.div
                        role="document"
                        className={
                            side
                                ? "absolute top-2 bottom-2 right-2 w-[min(28rem,calc(100%-1rem))] flex flex-col rounded-[1.75rem] bg-base-200 shadow-2xl"
                                : centre
                                  ? "absolute inset-0 m-auto h-fit w-[min(32rem,calc(100%-2rem))] max-h-[85dvh] flex flex-col rounded-[1.75rem] bg-base-200 shadow-2xl"
                                  : "absolute inset-x-0 bottom-0 max-h-[92dvh] flex flex-col rounded-t-[1.75rem] bg-base-200 shadow-2xl"
                        }
                        initial={away}
                        animate={open ? { x: 0, y: 0, opacity: 1, scale: 1 } : away}
                        transition={centre ? { duration: 0.18, ease: "easeOut" } : sheetSpring}
                        drag={wide ? false : "y"}
                        dragListener={false}
                        dragControls={dragControls}
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={{ top: 0.05, bottom: 0.7 }}
                        onDragEnd={onDragEnd}
                        onAnimationComplete={() => {
                            if (!open && dialogRef.current?.open) dialogRef.current.close();
                        }}
                    >
                        {!wide && (
                            <div
                                className="shrink-0 pt-3 pb-1 cursor-grab touch-none"
                                onPointerDown={(event) => dragControls.start(event)}
                                aria-hidden="true"
                            >
                                <span className="block w-8 h-1 mx-auto rounded-full bg-base-content/40" />
                            </div>
                        )}

                        <header className="shrink-0 flex items-center gap-3 pl-5 pr-3 pt-2 pb-3 lg:pt-5">
                            <div className="grow min-w-0">
                                <h2 className="text-xl font-extrabold tracking-tight">{title}</h2>
                                {subtitle && <p className="text-sm text-base-content/70">{subtitle}</p>}
                            </div>

                            <button type="button" className="btn btn-ghost btn-circle" onClick={onClose} aria-label="Close">
                                <IconX />
                            </button>
                        </header>

                        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-5 space-y-2.5">
                            {children}
                        </div>

                        {footer && (
                            <footer className="shrink-0 flex flex-wrap justify-end gap-2 px-4 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))] border-t border-base-300">
                                {footer}
                            </footer>
                        )}
                    </motion.div>
                </dialog>
            </MotionConfig>
        </Portal>
    );
};

/** A card inside a sheet, for one group of related fields */
export const SheetSection = ({ heading, icon, children }: PropsWithChildren<{ heading: string; icon?: ReactNode }>) => (
    <section className="rounded-[1.25rem] bg-base-100 p-3.5 space-y-3">
        <h3 className="flex items-center gap-2 text-sm font-extrabold text-base-content/70">
            {icon}
            {heading}
        </h3>

        {children}
    </section>
);
